import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useQuery } from 'react-query';
import ArrearTable from 'components/ui/tables/ArrearTable';
import useTopPanel from 'hooks/useTopPanel';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import ArrearsFilterDrawer from 'components/dashboard/ArrearsFilterDrawer';
import SearchInputNavbar from 'components/SearchInputNavbar';
import ObjectFilter from 'components/ui/filters/items/ObjectFilter';
import useAuth from 'hooks/useAuth';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useNotification from 'hooks/useNotification';

// Qarzdorlik filtrlari brauzerda saqlanadi — sahifa qayta ochilganda
// oxirgi tanlov tiklanadi. Faqat shu sahifaga tegishli.
const FILTER_STORAGE_KEY = 'arrears-filters';

// Bular filtr emas: sahifa raqami va saralash. Ular na saqlanadi, na
// "manzilda filtr bor" deb hisoblanadi. Aks holda jadvalni bir marta
// saralash kifoya edi — manzilda order/order_field paydo bo'lib,
// saqlangan filtr tiklanmay qolardi.
const NON_FILTER_KEYS = ['page', 'order', 'order_field'];

const onlyFilters = (source) => {
  const params = new URLSearchParams(source);
  NON_FILTER_KEYS.forEach((key) => params.delete(key));

  return params;
};

const Arrears = () => {
  const [{ user }] = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Birinchi yurishda saqlamaymiz — aks holda tiklashdan oldin bo'sh
  // qiymat yozilib, eski filtr yo'qolib ketardi.
  const skipFirstSave = useRef(true);

  // Tiklash: manzilda FILTR parametri bo'lmaganda. `page` hisobga
  // olinmaydi — jadval uni o'zi qo'yib yuborishi mumkin va shu sabab
  // tiklash ishlamay qolardi. Tayyor havola bilan kelingan bo'lsa
  // (filtr ko'rsatilgan) — tegilmaydi.
  useEffect(() => {
    // Manzilda FILTR bo'lsa — tegilmaydi (tayyor havola bilan kelingan).
    if (onlyFilters(window.location.search).toString()) return;

    try {
      const saved = onlyFilters(localStorage.getItem(FILTER_STORAGE_KEY) || '');
      if (!saved.toString()) return;

      // Saralash va sahifa raqami saqlanib qoladi, ustiga filtrlar qo'shiladi
      const next = new URLSearchParams(window.location.search);
      saved.forEach((value, key) => next.append(key, value));
      setSearchParams(next, { replace: true });
    } catch (e) {
      // localStorage yopiq bo'lsa (private rejim) — filtrsiz ochilaveradi
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Saqlash: har safar filtr o'zgarganda. `page` saqlanmaydi — qaytib
  // kelganda doim birinchi sahifadan boshlansin.
  useEffect(() => {
    if (skipFirstSave.current) {
      skipFirstSave.current = false;
      return;
    }

    try {
      localStorage.setItem(
        FILTER_STORAGE_KEY,
        onlyFilters(searchParams).toString(),
      );
    } catch (e) {
      // saqlanmasa ham joriy sessiyada ishlayveradi
    }
  }, [searchParams]);
  const [refetch, setRefetch] = useState(false);
  const [open, setOpen] = useState(false);
  const { search } = useLocation();
  const { setComponent } = useTopPanel();
  const { t, i18n } = useTranslation();
  const [openFilter, setOpenFilter] = useState(false);
  const axiosPrivate = useAxiosPrivate();

  // Peniya ustuni faqat sozlamada yoqilgan bo'lsa ko'rinadi
  const { data: penaltySetting } = useQuery({
    queryKey: ['/admin/penalty-settings'],
    queryFn: async () => {
      const response = await axiosPrivate.get('/admin/penalty-settings');
      return response.data.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const showPenalty = penaltySetting?.show_on_contract ?? true;
  const sendNotification = useNotification();
  // SMS: belgilangan qarzdorlar (contract_id) va yuborish oynasi
  const [selected, setSelected] = useState([]);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  // Ixtiyoriy matn: bo'sh qolsa — qarzdorlik shabloni yuboriladi (avvalgidek)
  const [smsText, setSmsText] = useState('');

  // Qarzdorlik SMS'ini yuborish: belgilanganlarga yoki (bo'sh bo'lsa)
  // joriy filtrlar bo'yicha hammasiga
  const onSendSms = async () => {
    setSmsSending(true);
    try {
      const response = await axiosPrivate.post(
        `/dictionary/debitors/sms${search || ''}`,
        JSON.stringify({ contract_ids: selected, text: smsText.trim() }),
        { headers: { 'Content-Type': 'application/json' } },
      );
      const data = response?.data?.data;
      if (response?.data?.status) {
        sendNotification({
          msg: t('arrears.sms.result', {
            sent: data?.sent ?? 0,
            skipped: data?.skipped ?? 0,
          }),
          variant: 'success',
        });
        setSelected([]);
        setSmsText('');
        setSmsOpen(false);
      }
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || error?.message,
        variant: 'error',
      });
    } finally {
      setSmsSending(false);
    }
  };

  useEffect(() => {
    setComponent(
      <div className='component-title flex gap-4 whitespace-nowrap items-center'>
        {t('arrears.title')}
        <div className='w-56'>
          <ObjectFilter allowClear />
        </div>
      </div>,
    );
  }, [i18n.language]);

  const newSearch = useMemo(() => {
    if (search?.length > 0) {
      return `?${search?.toString()}&token=${user?.access_token}`;
    }
    return `?token=${user?.access_token}`;
  }, [search, user?.access_token]);

  return (
    <div className='component-list-wrapper'>
      <div className='component-list-header mb-2'>
        <div className='header-actions-container pt-2 pb-2 flex flex-row items-center'>
          <div className='header-actions filter-box flex items-center'>
            <Button
              variant='base'
              startIcon={<i className='bi bi-filter' />}
              className='!mr-2'
              onClick={() => setOpenFilter((prev) => !prev)}
            >
              {t('common.button.filter')}
            </Button>
          </div>
          <div className='grow'>
            <SearchInputNavbar inputKey='name' fullWidth />
          </div>
          <div className='header-actions action-buttons-box flex items-center justify-center ml-4'>
            <Button
              variant='base'
              onClick={() => {
                setRefetch(true);
              }}
              disable={`${refetch}`}
            >
              <i
                className={`bi bi-arrow-repeat${
                  refetch ? ' animate-spin' : ''
                }`}
              />
            </Button>
            <Link
              to={`${process.env.REACT_APP_BACKEND_URL}/excel/debitors${newSearch}`}
              // target='_blank'
              className='no-underline'
            >
              <Button variant='base' className='!ml-2'>
                <i className='bi bi-download' />
              </Button>
            </Link>
            <Button
              variant='base'
              color='success'
              className='!ml-2 whitespace-nowrap'
              onClick={() => setSmsOpen(true)}
            >
              <i className='bi bi-chat-left-text' />
              {selected.length > 0 && (
                <span className='ml-1'>({selected.length})</span>
              )}
            </Button>
            <Button
              variant='base'
              className='!ml-2'
              onClick={() => setOpen(true)}
            >
              <i className='bi bi-gear' />
            </Button>
          </div>
        </div>
      </div>

      <div className='component-table-wrapper h-[calc(100vh-160px)]'>
        <ArrearTable
          emitRefetch={{ refetch, setRefetch }}
          sorting={true}
          selection={{ selected, setSelected }}
          dataPath='dictionary/debitors'
          emitTableColumns={{ open, setOpen }}
          tableName='dictionary/debitors'
          headCells={[
            { code: 'id', label: '#' },
            {
              code: 'name',
              sortable: true,
              label: t('common.table.contractName'),
            },
            { code: 'custom', sortable: true, label: t('common.table.custom') },
            { code: 'phone', label: t('common.table.phone') },
            { code: 'sum', sortable: true, label: t('common.table.arrears') },
            ...(showPenalty
              ? [{ code: 'peniya', label: t('common.table.penalty') }]
              : []),
            {
              code: 'date',
              sortable: true,
              label: t('common.table.debtPaymentDate'),
            },
          ]}
          columns={[
            { code: 'id' },
            { code: 'name' },
            { code: 'custom' },
            {
              code: 'phone',
              type: 'multipleFields',
              fields: ['phone', 'phone2'],
            },
            { code: 'sum' },
            ...(showPenalty
              ? [{ code: 'peniya', type: 'priceCurrency' }]
              : []),
            { code: 'date', type: 'date' },
          ]}
          actionContractView={true}
        />
      </div>
      <ArrearsFilterDrawer open={openFilter} setOpen={setOpenFilter} />

      {/* SMS yuborishni tasdiqlash oynasi */}
      <Dialog open={smsOpen} onClose={() => !smsSending && setSmsOpen(false)}>
        <DialogTitle>{t('arrears.sms.button')}</DialogTitle>
        <DialogContent>
          <div className='text-sm text-gray-700 min-w-[300px] md:min-w-[480px]'>
            {selected.length > 0
              ? t('arrears.sms.toSelected', { count: selected.length })
              : t('arrears.sms.toAll')}
          </div>
          {/* Ixtiyoriy matn — bo'sh qolsa qarzdorlik shabloni ketadi */}
          <div className='mt-4'>
            <label
              htmlFor='arrears-sms-text'
              className='block text-xs text-gray-500 mb-1'
            >
              {t('arrears.sms.customText')}
            </label>
            <textarea
              id='arrears-sms-text'
              rows={4}
              className='w-full base-border rounded-lg px-3 py-2 text-sm text-gray-700 bg-white outline-none focus:border-[#0057FF] resize-y disabled:bg-gray-50'
              value={smsText}
              onChange={(e) => setSmsText(e.target.value)}
              disabled={smsSending}
            />
            <div className='text-xs text-gray-400 mt-1'>
              {t('arrears.sms.customTextHint')}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant='base'
            disabled={smsSending}
            onClick={() => setSmsOpen(false)}
          >
            {t('common.button.cancel')}
          </Button>
          <Button
            variant='contained'
            color='success'
            disabled={smsSending}
            startIcon={
              smsSending ? (
                <CircularProgress size={14} color='inherit' />
              ) : (
                <i className='bi bi-send' />
              )
            }
            onClick={onSendSms}
          >
            {t('arrears.sms.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default memo(Arrears);
